/**
 * Tiện ích xử lý XSS cho toàn bộ hệ thống
 * Phòng chống XSS Stored
 * Created by: tbngoc - 04.03.2025
 */

import DOMPurify from 'dompurify';
import { sanitizeHtml } from './directives/safeHtml';

/**
 * Class chứa các tiện ích xử lý an toàn để chống XSS
 */
class XssUtils {
  /**
   * Làm sạch chuỗi HTML, loại bỏ tất cả các tags
   * Dùng khi chỉ muốn giữ lại nội dung text thuần túy
   * 
   * @param {String} text - Chuỗi cần làm sạch
   * @returns {String} Chuỗi đã được làm sạch, chỉ còn text thuần túy
   */
  stripTags(text) {
    if (!text) return '';
    return DOMPurify.sanitize(text, { ALLOWED_TAGS: [], KEEP_CONTENT: true });
  }

  /**
   * Escape HTML để hiển thị an toàn
   * Chuyển đổi các ký tự đặc biệt thành entity codes
   * 
   * @param {String} text - Chuỗi cần escape
   * @returns {String} Chuỗi đã được escape
   */
  escapeHtml(text) {
    if (!text) return '';
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Sanitize cho nội dung HTML chấp nhận một số thẻ cơ bản
   * Wrapper cho sanitizeHtml từ safeHtml.js
   * 
   * @param {String} html - HTML cần sanitize
   * @returns {String} HTML đã được sanitize
   */
  sanitizeHtml(html) {
    return sanitizeHtml(html);
  }

  /**
   * Làm sạch một URL để đảm bảo an toàn
   * Loại bỏ các protocol nguy hiểm như javascript:
   * 
   * @param {String} url - URL cần làm sạch
   * @returns {String} URL đã được làm sạch
   */
  sanitizeUrl(url) {
    if (!url) return '';
    
    // Loại bỏ các protocol nguy hiểm
    const dangerousProtocols = ['javascript:', 'data:', 'vbscript:', 'file:'];
    const lowerUrl = url.toLowerCase().trim();
    
    for (const protocol of dangerousProtocols) {
      if (lowerUrl.startsWith(protocol)) {
        return '#';
      }
    }
    
    return url;
  }

  /**
   * Làm sạch dữ liệu trước khi hiển thị trong các control
   * Dùng cho dữ liệu từ API trước khi hiển thị
   * 
   * @param {Object|Array} data - Dữ liệu cần làm sạch
   * @param {Object} options - Tùy chọn cấu hình
   * @returns {Object|Array} Dữ liệu đã được làm sạch
   */
  sanitizeData(data, options = {}) {
    if (!data) return data;
    
    const stringFields = options.stringFields || []; // Các trường cần xử lý như string
    const htmlFields = options.htmlFields || []; // Các trường cho phép HTML cơ bản
    const urlFields = options.urlFields || []; // Các trường URL cần xử lý
    
    const processSingleObject = (obj) => {
      if (!obj || typeof obj !== 'object') return obj;
      
      const result = { ...obj };
      
      for (const key in result) {
        if (result.hasOwnProperty(key)) {
          const value = result[key];
          
          if (typeof value === 'string') {
            if (htmlFields.includes(key)) {
              // Cho phép HTML cơ bản được sanitize
              result[key] = this.sanitizeHtml(value);
            } else if (urlFields.includes(key)) {
              // Làm sạch URL
              result[key] = this.sanitizeUrl(value);
            } else if (stringFields.includes(key) || stringFields.length === 0) {
              // Escape HTML hoặc mặc định xử lý tất cả các string fields
              result[key] = this.escapeHtml(value);
            }
          } else if (Array.isArray(value)) {
            // Xử lý đệ quy với mảng
            result[key] = value.map(item => 
              typeof item === 'object' ? processSingleObject(item) : item
            );
          } else if (value && typeof value === 'object') {
            // Xử lý đệ quy với object
            result[key] = processSingleObject(value);
          }
        }
      }
      
      return result;
    };
    
    if (Array.isArray(data)) {
      return data.map(item => processSingleObject(item));
    }
    
    return processSingleObject(data);
  }

  /**
   * Xử lý dữ liệu đầu vào từ người dùng trước khi gửi lên server
   * Loại bỏ các script và content nguy hiểm
   * 
   * @param {Object|Array} data - Dữ liệu từ form người dùng
   * @param {Object} options - Tùy chọn cấu hình
   * @returns {Object|Array} Dữ liệu đã được xử lý an toàn
   */
  sanitizeInput(data, options = {}) {
    return this.sanitizeData(data, {
      ...options,
      // Mặc định xử lý tất cả các trường string
      stringFields: options.stringFields || []
    });
  }
}

export default new XssUtils();
